import React, { useEffect, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { ProjectLayoutModel } from '@paragon/contract-library';
import { editLayoutModelProperties, getSchemaForProjectHierarchyModel, ModelProperty } from '@paragon/layout-models';

import { useAppDispatch } from './hooks';

const FIELD_GROUPS: readonly { readonly title: string; readonly fields: readonly string[] }[] = [
  { title: 'Contact', fields: ['customerName', 'contactName', 'contactPhone', 'contactEmail'] },
  { title: 'Project', fields: ['name', 'model', 'projectSalesman', 'organization', 'createdAt'] },
  { title: 'Location', fields: ['address', 'city', 'region', 'subdivision', 'lotNumber'] },
  {
    title: 'Building designer',
    fields: [
      'buildingDesignerName',
      'buildingDesignerAddress',
      'buildingDesignerCity',
      'buildingDesignerState',
      'buildingDesignerLicenseNumber',
    ],
  },
];

interface Props {
  readonly project: ProjectLayoutModel;
  readonly focusProjectName?: boolean;
  readonly onProjectNameFocused?: () => void;
}

export function ProjectDetailsFields({ project, focusProjectName, onProjectNameFocused }: Props) {
  const dispatch = useAppDispatch();
  const projectNameFieldRef = useRef<HTMLDivElement | null>(null);
  const projectSchema = getSchemaForProjectHierarchyModel(project);

  useEffect(() => {
    if (!focusProjectName) return;
    const input = projectNameFieldRef.current?.querySelector('input');
    if (!input) return;
    input.focus();
    input.select();
    onProjectNameFocused?.();
  }, [focusProjectName, onProjectNameFocused, project.guid]);

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      {FIELD_GROUPS.map((group) => (
        <Box key={group.title} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {group.title}
          </Typography>
          <Grid container spacing={2}>
            {group.fields.map((field) => (
              <Grid key={field} ref={field === 'name' ? projectNameFieldRef : undefined} size={6}>
                <ModelProperty
                  property={field as keyof ProjectLayoutModel}
                  model={project}
                  onEdit={(guid, editedProperties) => dispatch(editLayoutModelProperties(guid, editedProperties))}
                  modelSchema={projectSchema}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
